import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { ArrowRight, Brain, Cpu, Settings, Beaker, Zap, Users, Globe, Shield } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { FadeIn, StaggerContainer, StaggerItem } from '../PageTransition';

export const ProductsPage: React.FC = () => {
  const productCategories = [
    {
      title: 'AI Products',
      description: 'Cutting-edge artificial intelligence solutions for modern business challenges',
      icon: Brain,
      color: '#00CFFF',
      products: [
        
        { name: 'Equipment Failure Detection', path: '/products/ai/equipment-failure-detection', description: 'Predictive maintenance using machine learning' },
        { name: 'PPE Compliance Monitoring', path: '/products/ai/ppe-compliance-monitoring', description: 'Computer vision for safety compliance' },
        { name: 'Customer Review Summarizer', path: '/products/ai/customer-review-summarizer', description: 'Automated review analysis and insights' },
        { name: 'Agentic Loan Recommendation System', path: '/products/ai/agentic-loan-recommendation', description: 'Intelligent loan recommendation engine' },
      ]
    },
    {
      title: 'IT Products',
      description: 'Comprehensive IT solutions for business operations and management',
      icon: Cpu,
      color: '#0FFFA0',
      products: [
        { name: 'ManageRTC', path: '/products/it/ManageRTC', description: 'Complete human resource management system' },
        { name: 'Project Management Tool', path: '/products/it/project-management-tool', description: 'Streamlined project tracking and collaboration' },
        { name: 'Websites', path: '/products/it/websites', description: 'Custom web development solutions' },
        { name: 'Learning Management System', path: '/products/it/learning-management-system', description: 'Comprehensive educational platform' },
      ]
    },
    {
      title: 'IoT Products',
      description: 'Internet of Things solutions for smart monitoring and automation',
      icon: Settings,
      color: '#8A3FFC',
      products: [
        { name: 'Water Level IoT', path: '/products/iot/water-level-iot', description: 'Smart water level monitoring system' },
        { name: 'Medical Coding', path: '/products/iot/medical-coding', description: 'Automated medical coding solutions' },
      ]
    },
    {
      title: 'Life Science Products',
      description: 'Specialized solutions for life sciences and healthcare industries',
      icon: Beaker,
      color: '#ff6b6b',
      products: [
        { name: 'DEG Compliance', path: '/products/life-science/deg-compliance', description: 'Regulatory compliance management' },
      ]
    }
  ];

  const features = [
    {
      icon: Zap,
      title: 'High Performance',
      description: 'Optimized for speed and efficiency across all our products'
    },
    {
      icon: Shield,
      title: 'Enterprise Security',
      description: 'Bank-level security and compliance for all business needs'
    },
    {
      icon: Users,
      title: 'User-Friendly',
      description: 'Intuitive interfaces designed for seamless user experience'
    },
    {
      icon: Globe,
      title: 'Scalable Solutions',
      description: 'Products that grow with your business needs'
    }
  ];

  return (
    <div className="min-h-screen pt-24">
      {/* Hero Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-secondary/10" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <FadeIn>
            <motion.h1
              className="text-5xl md:text-7xl mb-6 neon-text"
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              transition={{ duration: 1, delay: 0.2 }}
            >
              Our <span className="text-accent">Products</span>
            </motion.h1>
            
            <motion.p
              className="text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto mb-8"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.5 }}
            >
              Comprehensive suite of AI-powered, IT, IoT, and Life Science solutions 
              designed to transform your business operations.
            </motion.p>

            <motion.div
              className="flex flex-col sm:flex-row gap-4 justify-center items-center"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
            >
              <Link 
                to="/contact"
                className="bg-primary hover:bg-primary/80 text-primary-foreground px-8 py-4 rounded-lg transition-all duration-300 neon-glow flex items-center gap-2"
              >
                Get Started
                <ArrowRight className="w-5 h-5" />
              </Link>
            </motion.div>
          </FadeIn>
        </div>
      </section>

      {/* Product Categories */}
      <section className="py-24 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <div className="text-center mb-16">
              <h2 className="mb-4">Product Categories</h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Explore our diverse range of innovative solutions across different technology domains.
              </p>
            </div>
          </FadeIn>

          <StaggerContainer>
            <div className="space-y-16">
              {productCategories.map((category) => (
                <StaggerItem key={category.title}>
                  <motion.div
                    className="glass-strong rounded-2xl p-8"
                    whileHover={{ scale: 1.02 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="grid lg:grid-cols-3 gap-8 items-center">
                      <div className="lg:col-span-1">
                        <div className="flex items-center gap-4 mb-4">
                          <div 
                            className="w-16 h-16 rounded-lg flex items-center justify-center"
                            style={{ backgroundColor: `${category.color}20` }}
                          >
                            <category.icon 
                              className="w-8 h-8"
                              style={{ color: category.color }}
                            />
                          </div>
                          <div>
                            <h3 className="text-2xl font-bold mb-2" style={{ color: category.color }}>
                              {category.title}
                            </h3>
                          </div>
                        </div>
                        <p className="text-muted-foreground mb-6">
                          {category.description}
                        </p>
                      </div>

                      <div className="lg:col-span-2">
                        <div className="grid sm:grid-cols-2 gap-4">
                          {category.products.map((product) => (
                            <Link
                              key={product.name}
                              to={product.path}
                              className="block group"
                            >
                              <Card className="h-full glass hover:glass-strong transition-all duration-300 hover:scale-105">
                                <CardContent className="p-6">
                                  <h4 className="font-semibold mb-2 group-hover:text-primary transition-colors">
                                    {product.name}
                                  </h4>
                                  <p className="text-sm text-muted-foreground">
                                    {product.description}
                                  </p>
                                  <div className="flex items-center mt-4 text-primary opacity-0 group-hover:opacity-100 transition-opacity">
                                    <span className="text-sm">Learn more</span>
                                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                                  </div>
                                </CardContent>
                              </Card>
                            </Link>
                          ))}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                </StaggerItem>
              ))}
            </div>
          </StaggerContainer>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 relative">
        <div className="absolute inset-0 bg-gradient-to-r from-primary/5 to-secondary/5" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <FadeIn>
            <div className="text-center mb-16">
              <h2 className="mb-4">Why Choose Our Products?</h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Built with cutting-edge technology and designed for enterprise-grade performance.
              </p>
            </div>
          </FadeIn>

          <StaggerContainer>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {features.map((feature) => (
                <StaggerItem key={feature.title}>
                  <Card className="glass-strong h-full hover:scale-105 transition-all duration-300 group">
                    <CardContent className="p-6 text-center">
                      <div className="w-16 h-16 mx-auto mb-4 rounded-lg bg-primary/20 flex items-center justify-center">
                        <feature.icon className="w-8 h-8 text-primary" />
                      </div>
                      <h3 className="text-xl font-semibold mb-3 group-hover:text-primary transition-colors">
                        {feature.title}
                      </h3>
                      <p className="text-muted-foreground">
                        {feature.description}
                      </p>
                    </CardContent>
                  </Card>
                </StaggerItem>
              ))}
            </div>
          </StaggerContainer>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative">
        <div className="absolute inset-0 bg-gradient-to-r from-primary/10 via-accent/5 to-secondary/10" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <FadeIn>
            <h2 className="mb-6">Ready to Transform Your Business?</h2>
            <p className="text-xl text-muted-foreground mb-8">
              Discover how our innovative products can streamline your operations and drive growth.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="bg-primary hover:bg-primary/80 text-primary-foreground px-8 py-4 rounded-lg transition-all duration-300 neon-glow text-lg"
              >
                Schedule a Demo
              </Link>
              <Link
                to="/case-studies"
                className="border border-accent text-accent hover:bg-accent/10 px-8 py-4 rounded-lg transition-all duration-300 text-lg"
              >
                View Case Studies
              </Link>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
};